import { GCM_NONCE_LEN, GCM_TAG_LEN, fromBase64Url, isJsonObject, toBase64Url } from './bytes';
import { assertVerified } from './envelope';
import { KeyRefusedError, KeyResponseInvalidError, KeyServiceUnavailableError } from './errors';
import {
  idTokenOrSignIn,
  isDenyBody,
  isFiniteNumber,
  keyServiceError,
  lockGraceSecondsOrZero,
  postJson,
  readJson,
  requireString,
  type KeyRequestBody,
} from './keyApi';
import { ECDH_P256, WRAP_ALG, importServerPublicKey, unwrapDataKey } from './keyUnwrap';

import type {
  AcceptedPiiNoticeVersion,
  EnvelopeHeader,
  KeyGrant,
  KeyServiceDeps,
  VerifiedEnvelope,
} from './types';

const P256_UNCOMPRESSED_POINT_LEN = 65;
const DATA_KEY_LEN = 32;
const WRAPPED_KEY_LEN = GCM_NONCE_LEN + DATA_KEY_LEN + GCM_TAG_LEN;

export interface KeyRequestOptions {
  piiNoticeVersion?: AcceptedPiiNoticeVersion;
  signal?: AbortSignal;
}

interface ViewerKey {
  privateKey: CryptoKey;
  publicRaw: Uint8Array;
}

interface KeyServiceReply {
  status: number;
  payload: unknown;
}

export async function requestKey(
  verified: VerifiedEnvelope,
  deps: KeyServiceDeps,
  { piiNoticeVersion, signal }: KeyRequestOptions = {},
): Promise<KeyGrant> {
  assertVerified(verified);
  const { header } = verified;
  const viewerKey = await ephemeralViewerKey();
  const body = keyRequestBody(header, viewerKey.publicRaw, piiNoticeVersion);
  const idToken = await idTokenOrSignIn(deps);
  signal?.throwIfAborted();
  const { status, payload } = await sendKeyRequest(deps, idToken, body, signal);
  if (isDenyBody(payload)) throw new KeyRefusedError(payload.reason);
  if (status !== 200) throw keyServiceError(status, payload);
  return parseGrant(payload, header, viewerKey);
}

async function ephemeralViewerKey(): Promise<ViewerKey> {
  const { privateKey, publicKey } = await crypto.subtle.generateKey(ECDH_P256, false, [
    'deriveBits',
  ]);
  const publicRaw = new Uint8Array(await crypto.subtle.exportKey('raw', publicKey));
  return { privateKey, publicRaw };
}

function keyRequestBody(
  header: EnvelopeHeader,
  viewerPublicRaw: Uint8Array,
  piiNoticeVersion: AcceptedPiiNoticeVersion | undefined,
): KeyRequestBody {
  const body: KeyRequestBody = {
    downloadId: header.downloadId,
    viewerPublicKey: toBase64Url(viewerPublicRaw),
    launcherVersion: header.launcherVer,
  };
  if (piiNoticeVersion !== undefined) {
    body.piiNoticeVersion = piiNoticeVersion;
  }
  return body;
}

async function sendKeyRequest(
  deps: KeyServiceDeps,
  idToken: string,
  body: KeyRequestBody,
  signal: AbortSignal | undefined,
): Promise<KeyServiceReply> {
  try {
    const response = await postJson(deps, '/key', idToken, body, signal);
    return { status: response.status, payload: await readJson(response) };
  } catch {
    signal?.throwIfAborted();
    throw new KeyServiceUnavailableError();
  }
}

async function parseGrant(
  payload: unknown,
  header: EnvelopeHeader,
  viewerKey: ViewerKey,
): Promise<KeyGrant> {
  if (!isJsonObject(payload) || payload.allow !== true) {
    throw new KeyResponseInvalidError('allow');
  }
  if (payload.alg !== WRAP_ALG) {
    throw new KeyResponseInvalidError('alg');
  }
  if (payload.downloadId !== header.downloadId) {
    throw new KeyResponseInvalidError('downloadId');
  }
  const jti = requireString(payload, 'jti');
  if (jti === '') {
    throw new KeyResponseInvalidError('jti');
  }
  const serverPublicRaw = requireBytes(payload, 'serverPublicKey', P256_UNCOMPRESSED_POINT_LEN);
  const wrappedKey = requireBytes(payload, 'wrappedKey', WRAPPED_KEY_LEN);
  const { secondsRemaining } = payload;
  if (!isFiniteNumber(secondsRemaining) || secondsRemaining < 0) {
    throw new KeyResponseInvalidError('secondsRemaining');
  }
  const labels = {
    docName: requireString(payload, 'docName'),
    docScope: requireString(payload, 'docScope'),
    viewerName: requireString(payload, 'viewerName'),
    openedAt: requireString(payload, 'openedAt'),
    sourceIp: requireString(payload, 'sourceIp'),
  };

  let serverPublicKey: CryptoKey;
  try {
    serverPublicKey = await importServerPublicKey(serverPublicRaw);
  } catch (cause) {
    throw new KeyResponseInvalidError('serverPublicKey', { cause });
  }
  let dataKey: CryptoKey;
  try {
    dataKey = await unwrapDataKey({
      viewerPrivateKey: viewerKey.privateKey,
      viewerPublicRaw: viewerKey.publicRaw,
      serverPublicKey,
      serverPublicRaw,
      wrappedKey,
      downloadId: header.downloadId,
      jti,
    });
  } catch (cause) {
    throw new KeyResponseInvalidError('wrappedKey', { cause });
  }

  return {
    dataKey,
    session: {
      jti,
      secondsRemaining,
      lockGraceSeconds: lockGraceSecondsOrZero(payload.lockGraceSeconds),
    },
    ...labels,
  };
}

function requireBytes(payload: Record<string, unknown>, name: string, length: number): Uint8Array {
  const bytes = fromBase64Url(requireString(payload, name));
  if (bytes?.length !== length) {
    throw new KeyResponseInvalidError(name);
  }
  return bytes;
}
